// ── Mozgás-réteg (2026-08-06) ──────────────────────────────────────────────
//
// A Dashboard „full" mozgás-szintje EZEKRE a hookokra épül. Három szabály, ami
// a döntés része volt, és amit tartani kell:
//
//  1. ⚠ MINDEN mozgás a `prefers-reduced-motion` mögött van. Nem opció: a
//     vesztibuláris érzékenység valós hozzáférhetőségi kérdés. Ha új animációt
//     írsz, a `usePrefersReducedMotion()`-t KÉRDEZD MEG, ne csak CSS-ben védd —
//     a JS-vezérelt számláló-animációt a CSS media query nem állítja meg.
//
//  2. ⚠ Animáció CSAK mountkor, adatfrissítéskor SOHA. A Recharts alapból
//     újraanimál minden adatváltozásnál; egy 60 mp-es `staleTime` melletti
//     refetch így minden percben újrarajzolná a chartot a felhasználó szeme
//     előtt. Erre való a `useEnterAnimation()`.
//
//  3. ⚠ Pénzügyi számon SOHA nincs loop-animáció. A pörgő szám egyszer fut le,
//     és megáll. A folyamatos mozgás bizonytalanságot sugall — banki
//     kontextusban ez azonnal olcsóvá teszi a terméket.
import { useEffect, useRef, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/** A felhasználó csökkentett mozgást kért-e. Élőben követi a rendszer-beállítást. */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(QUERY).matches === true,
  );
  useEffect(() => {
    const mq = window.matchMedia?.(QUERY);
    if (!mq) return undefined;
    const on = (e) => setReduced(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

/**
 * Belépő animáció kapcsolója: `true` a mount utáni `duration` ideig, utána `false`.
 *
 * A Recharts `isAnimationActive`-jába megy. Azért kell, mert a Recharts nem tud
 * „csak egyszer animálj" módot: adatváltozásra újraindítja a rajzolást.
 */
export function useEnterAnimation(duration = 600) {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState(!reduced);
  useEffect(() => {
    if (reduced) { setActive(false); return undefined; }
    // +200 ms ráhagyás: a Recharts a saját `animationBegin` késleltetése után indul
    const t = setTimeout(() => setActive(false), duration + 200);
    return () => clearTimeout(t);
    // ⚠ A dependency-lista SZÁNDÉKOSAN üres: ez EGYSZER fut, mountkor. Ha
    // beleírod a `duration`/`reduced` értékeket, minden változásuk újraindítja a
    // belépő animációt — pont azt a viselkedést hozva vissza, amit elkerülünk.
  }, []);
  return active && !reduced;
}

/**
 * Szám felpörgetése 0-ról a célértékre.
 *
 * ⚠ A hívóhelyen a szám legyen `--mono` + `tabular-nums`, különben a pörgés
 * közben változik a karakterszélesség, és a kártya ugrál. (Ez a különbség egy
 * kellemes és egy olcsó animáció között.)
 *
 * Csökkentett mozgásnál azonnal a végértéket adja vissza — nem 0-t.
 */
export function useCountUp(target, { duration = 620, decimals = 0 } = {}) {
  const reduced = usePrefersReducedMotion();
  const safe = Number.isFinite(Number(target)) ? Number(target) : 0;
  const [value, setValue] = useState(reduced ? safe : 0);
  const from = useRef(0);
  const raf = useRef(0);

  useEffect(() => {
    if (reduced) { setValue(safe); return undefined; }
    const start = performance.now();
    const a = from.current;
    const step = (now) => {
      const k = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - k, 4);          // easeOutQuart
      const v = a + (safe - a) * eased;
      setValue(decimals ? v : Math.round(v));
      if (k < 1) raf.current = requestAnimationFrame(step);
      else from.current = safe;
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [safe, duration, decimals, reduced]);

  return value;
}
